import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Feather, Trophy } from "lucide-react";
import { API, useAuth } from "@/App";

/**
 * Compact Founding 100 teaser card — rendered on the landing page.
 *
 * Deliberately NOT a full ledger. It exists to communicate:
 *   scarcity + permanence + prestige + legacy
 * and hand off to `/founding-100` for the actual charter.
 *
 * Every number here is pulled live from
 *   `GET /api/founder/public/summary`
 * so there is exactly one authoritative source across the whole site.
 */
export default function FoundingHomepageTeaser() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/founder/public/summary`);
        if (!cancelled) setSummary(r.data);
      } catch {
        // Silent — the teaser just doesn't render if the API is down.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!summary) return null;

  const { claimed, remaining, total, percent, closed } = summary;
  const canClaim = !closed;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
      data-testid="founding-homepage-teaser"
    >
      <div className="relative overflow-hidden rounded-3xl bg-tbc-cream border border-tbc-gold/25 shadow-[0_18px_44px_-24px_rgba(13,27,52,0.35)] p-6 sm:p-10 lg:p-12">
        {/* Ornamental corners — pure CSS, matches the ledger card style */}
        <div className="absolute top-4 left-4 h-6 w-6 border-t border-l border-tbc-gold/40 rounded-tl-lg pointer-events-none" />
        <div className="absolute top-4 right-4 h-6 w-6 border-t border-r border-tbc-gold/40 rounded-tr-lg pointer-events-none" />
        <div className="absolute bottom-4 left-4 h-6 w-6 border-b border-l border-tbc-gold/40 rounded-bl-lg pointer-events-none" />
        <div className="absolute bottom-4 right-4 h-6 w-6 border-b border-r border-tbc-gold/40 rounded-br-lg pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_auto_minmax(0,1fr)] gap-6 lg:gap-10 items-center">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.28em] font-semibold text-tbc-navy/70 flex items-center gap-2">
              <Feather className="h-3.5 w-3.5 text-tbc-gold" />
              The Founding 100
            </div>
            <h2
              className="mt-2 font-heading text-tbc-navy leading-[1.05]"
              style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
            >
              One hundred names.
              <br />
              <span className="italic text-tbc-gold">Written once.</span>
            </h2>
            <p className="mt-3 max-w-[52ch] font-body text-tbc-navy/75 leading-relaxed text-sm sm:text-base">
              The first 100 businesses to invest in the vision of The BLK
              Community are permanently recorded in our Founding Charter.
              Founding numbers are historical — never reissued, never
              recycled.
            </p>
          </div>

          {/* Live counter block */}
          <div className="flex-none flex items-center gap-6 lg:pl-4 lg:border-l border-tbc-gold/30">
            <div className="text-center">
              <div
                className="font-heading text-3xl sm:text-5xl text-tbc-navy leading-none tabular-nums"
                data-testid="founding-teaser-claimed"
              >
                {claimed}
              </div>
              <div className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.16em] font-semibold text-tbc-navy/60">
                / {total} Claimed
              </div>
            </div>
            <div className="h-10 w-px bg-tbc-gold/40" />
            <div className="text-center">
              <div
                className="font-heading text-3xl sm:text-5xl text-tbc-navy leading-none tabular-nums"
                data-testid="founding-teaser-remaining"
              >
                {remaining}
              </div>
              <div className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.16em] font-semibold text-tbc-navy/60">
                Positions Remain
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2 items-stretch lg:items-end">
            <Link
              to="/founding-100"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-tbc-navy px-5 py-3 text-white font-body font-semibold hover:bg-tbc-navy/90 transition-colors shadow-md"
              data-testid="founding-teaser-view-cta"
            >
              View the Founding 100 <span aria-hidden>→</span>
            </Link>
            {canClaim && user && (
              <Link
                to="/pricing#founding"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-tbc-gold bg-white px-5 py-2.5 text-tbc-navy font-body font-semibold hover:bg-tbc-gold/15 transition-colors"
                data-testid="founding-teaser-claim-cta"
              >
                Claim Your Entry <span aria-hidden>→</span>
              </Link>
            )}
            {closed && (
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-tbc-gold flex items-center justify-end gap-1">
                <Trophy className="h-3.5 w-3.5" />
                Charter closed · {total} / {total}
              </div>
            )}
            <div className="text-[10px] text-tbc-navy/50 mt-1 hidden lg:block">
              {percent}% inscribed
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
